import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { Modal } from "@mui/material";
import { useTranslation } from "react-i18next";

import Confetti from "react-confetti";
import { ShareBabyPeekPDFPayment } from "./Services/BabyPeekService";
import {
  BabyPeekShareTraitModalWrapper,
  BabyPeekShareTraitModalTitle,
  BabyPeekShareTraitModalSubTitle,
  BabyPeekShareTraitModalSubTitleInput,
  BabyPeekFilledButton,
  BabyPeekShareErrorText,
  BabyPeekShareReceiptWrapper,
  BabyPeekReceiptContainer,
  BabyPeekShareTitle,
  BabyPeekReceiptHeader,
  BabyPeekShareTraitModalHelp,
} from "./BabyPeekTrait.styles";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { validateEmail } from "../../utils/hooks/helpers";

import { BabyPeekShareAck } from "./BabyPeekStripe.styles";
import { GreenFullCheckIcon } from "../../assets/iconComponents/GreenFullCheckIcon";
import { getUserAuth } from "../../recoil/selectors";

export const BabyPeekDownloadModal: React.FC<{
  shareTraitModal: boolean;
  setShareTraitModal: (show: boolean) => void;
}> = ({ setShareTraitModal, shareTraitModal }) => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const { deviceType } = useDeviceHook();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const auth = useRecoilValue(getUserAuth);

  const { babypeekPK } = useParams<{
    babypeekPK: string;
  }>();

  const shareTrait = async () => {
    if (!validateEmail(email)) {
      setInvalidEmail(true);
    } else if (email) {
      setLoading(true);
      const response = await ShareBabyPeekPDFPayment(
        babypeekPK,
        name ?? "",
        email,
        i18n.language.startsWith("es") ? "es" : "en",
        auth?.access_token,
      );
      if (response?.ok) {
        setSubmitted(true);
        setName("");
        setEmail("");
      }
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        open={shareTraitModal}
        onClose={() => setShareTraitModal(false)}
        style={{
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
        }}
      >
        <>
          {submitted && (
            <BabyPeekReceiptContainer deviceType={deviceType}>
              <Confetti
                height={220}
                numberOfPieces={100}
                gravity={0.1}
                initialVelocityX={5}
                initialVelocityY={5}
                recycle
                colors={[
                  "#FF00C7",
                  "#FFCCF4",
                  "#544DFD",
                  "#FFE500",
                  "#8A69F2",
                  "#FDF075",
                ]}
                width={400}
              />

              <BabyPeekShareReceiptWrapper deviceType={deviceType}>
                <>
                  <BabyPeekReceiptHeader>
                    <>
                      {" "}
                      <GreenFullCheckIcon />
                      <BabyPeekShareTitle>
                        {t("babyPeek.download.successTitle")}
                      </BabyPeekShareTitle>{" "}
                      <BabyPeekFilledButton
                        onClick={() => setShareTraitModal(false)}
                        style={{
                          justifySelf: "center",
                          width: `100%`,
                          height: "44px",
                        }}
                      >
                        {t("babyPeek.buttons.ok")}
                      </BabyPeekFilledButton>
                    </>
                  </BabyPeekReceiptHeader>
                </>
              </BabyPeekShareReceiptWrapper>
            </BabyPeekReceiptContainer>
          )}
          {!submitted && (
            <BabyPeekShareTraitModalWrapper deviceType={deviceType}>
              <div style={{ display: "grid", width: "100%", gridGap: "4px" }}>
                <BabyPeekShareTraitModalTitle
                  style={{ justifyContent: "start" }}
                >
                  {submitted
                    ? t("babyPeek.download.resultsShared")
                    : t("babyPeek.download.title")}
                </BabyPeekShareTraitModalTitle>
                <BabyPeekShareTraitModalSubTitle>
                  {t("babyPeek.download.emailDescription")}
                </BabyPeekShareTraitModalSubTitle>
              </div>

              <div style={{ display: "grid", gridGap: "16px" }}>
                <div style={{ width: "100%" }}>
                  <BabyPeekShareTraitModalSubTitle>
                    {t("babyPeek.download.recipientEmail")}
                  </BabyPeekShareTraitModalSubTitle>
                  <BabyPeekShareTraitModalSubTitleInput
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setInvalidEmail(false);
                    }}
                  />
                  {invalidEmail && (
                    <BabyPeekShareErrorText>
                      {t("babyPeek.download.invalidEmail")}
                    </BabyPeekShareErrorText>
                  )}
                </div>
                <div style={{ width: "100%" }}>
                  <BabyPeekShareTraitModalSubTitle>
                    {t("babyPeek.download.nameReport")}
                  </BabyPeekShareTraitModalSubTitle>
                  <BabyPeekShareTraitModalSubTitleInput
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                    }}
                  />
                  <BabyPeekShareTraitModalHelp>
                    {t("babyPeek.download.nameHelp")}
                  </BabyPeekShareTraitModalHelp>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto",
                    gap: "16px",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gap: "16px",
                      marginTop: "6px",
                    }}
                  >
                    {loading ? (
                      <SpinnerLoading />
                    ) : (
                      <BabyPeekFilledButton onClick={() => shareTrait()}>
                        {t("babyPeek.buttons.shareNow")}
                      </BabyPeekFilledButton>
                    )}
                    <BabyPeekFilledButton
                      onClick={() => setShareTraitModal(false)}
                      style={{ background: "white", color: "blue" }}
                    >
                      {t("babyPeek.buttons.cancel")}
                    </BabyPeekFilledButton>
                  </div>
                  <BabyPeekShareAck>
                    {t("babyPeek.download.acknowledgement")}
                  </BabyPeekShareAck>
                </div>
              </div>
            </BabyPeekShareTraitModalWrapper>
          )}
        </>
      </Modal>
    </>
  );
};
